import { ExpandedTooltip, Icon } from '@livable/saas-react-components';
import { IconButton } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useStore } from 'src/stores';
import { getCurrentCompanyId } from 'src/stores/billAutomation/currentCompanyId';
import { tierOf } from './tiers';
import { useSubscriptionTier } from './useSubscriptionTier';
import useStyles from './styles';
export { UpgradeNow } from './UpgradeNow';

// The AP Push switch's info icon: 20px inside the theme's small icon button.
const INFO_BUTTON = {
  padding: '2px',
  '& .MuiSvgIcon-root': {
    width: '20px',
    height: '20px'
  }
};

/**
 * A plan's name with what it includes behind an info icon, as the AP Push switch says what it does (PRD V3, FR-11).
 * Renders nothing for a name it doesn't know.
 */
export const SubscriptionTierBadge = _ref => {
  let tier = _ref.tier,
    className = _ref.className;
  const classes = useStyles();
  const entry = tierOf(tier);
  if (!entry) return null;

  // Its own column, so the plan's name sits above what it includes whatever layout the tooltip gives its content.
  const tip = /*#__PURE__*/React.createElement("span", {
    className: classes.tip
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.tipTitle
  }, entry.name), /*#__PURE__*/React.createElement("span", null, entry.tip));
  return /*#__PURE__*/React.createElement("span", {
    className: className ? "".concat(classes.badge, " ").concat(className) : classes.badge,
    "data-testid": "subscription-tier"
  }, entry.name, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    type: "grey",
    title: tip,
    placement: "top",
    describeChild: true
  }, /*#__PURE__*/React.createElement(IconButton, {
    size: "small",
    "aria-label": "About ".concat(entry.name),
    sx: INFO_BUTTON
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "info",
    state: "info"
  }))));
};

/**
 * The named company's plan, read from the tier route (LB-6228). Nothing shows while it's unknown, when it can't be
 * read, or with billing options off.
 */
export const CompanySubscriptionTier = observer(_ref2 => {
  let companyId = _ref2.companyId,
    className = _ref2.className;
  const plan = useSubscriptionTier(companyId);
  return plan ? /*#__PURE__*/React.createElement(SubscriptionTierBadge, {
    tier: plan.tier,
    className: className
  }) : null;
});

/**
 * The plan of the company on screen, for an observer component. Every company switch, the Livy picker's included,
 * writes the new company to localStorage and then sets `homeDashboardStore.companyAccessId`; reading that id here makes
 * the switch re-read the company, with or without a page reload.
 */
export const useCurrentSubscriptionTier = () => {
  const store = useStore();
  // Observed, so a switch re-renders the caller; the company itself is localStorage's, which the switch writes first.
  const companyAccessId = store.homeDashboardStore.companyAccessId;
  return useSubscriptionTier(companyAccessId ? getCurrentCompanyId() : '');
};

/** The plan of the company on screen. */
export const CurrentSubscriptionTier = observer(_ref3 => {
  let className = _ref3.className;
  const plan = useCurrentSubscriptionTier();
  return plan ? /*#__PURE__*/React.createElement(SubscriptionTierBadge, {
    tier: plan.tier,
    className: className
  }) : null;
});